import { useEffect, useMemo, useRef, useState } from 'react';
import { apiBusca, type Achado, type Busca as Resultado } from '../lib/api.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { Icone } from './Icone.tsx';
import '../estilos/linha-rapida-2026.css';

/**
 * A busca global.
 *
 * ## Por que um painel, e não uma caixa fixa no cabeçalho
 *
 * O cabeçalho já leva logo, seis abas com ícone e o menu do usuário: numa
 * janela de 1440px sobra pouco, e entre 1100 e 1300px não sobra nada. Uma caixa
 * permanente espremeria as abas justamente na largura de notebook. A lupa custa
 * 40px e funciona igual no celular. Decisão do Richard, 15/08/2026.
 *
 * ## O atraso de 220ms não é economia de servidor
 *
 * É para a lista parar de pular embaixo do dedo. Sem ele, cada letra troca o
 * conteúdo e a pessoa clica no que não queria — o resultado que estava sob o
 * cursor virou outro entre o clique e o soltar.
 *
 * ## Pedido que chega fora de hora é descartado
 *
 * Duas buscas em voo podem voltar trocadas: "re" depois de "reuniao". Cada
 * resposta confere se ainda é a mais recente antes de aparecer na tela.
 */

const ATRASO = 220;

export function Busca({
  aoNavegar,
  variante = 'icone',
}: {
  aoNavegar: (caminho: string) => void;
  /** ⭐ `campo`: no menu lateral, com a palavra e o atalho escritos (14/09/2026). */
  variante?: 'icone' | 'campo';
}) {
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      // Ctrl+K / ⌘K abre de qualquer lugar. A barra sozinha também, mas só
      // quando não se está digitando — senão engoliria a barra de "N2/N3".
      const alvo = e.target as HTMLElement | null;
      const digitando =
        alvo?.tagName === 'INPUT' ||
        alvo?.tagName === 'TEXTAREA' ||
        alvo?.tagName === 'SELECT' ||
        alvo?.isContentEditable;

      if ((e.key === 'k' || e.key === 'K') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setAberto(true);
        return;
      }
      if (e.key === '/' && !digitando && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setAberto(true);
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  return (
    <>
      {variante === 'campo' ? (
        <button
          type="button"
          className="busca-campo"
          onClick={() => setAberto(true)}
          title="Procurar no Hub (Ctrl+K ou /)"
          aria-label="Procurar no Hub"
        >
          <Icone nome="lupa" tamanho={16} />
          <span>Buscar</span>
          <kbd>Ctrl K</kbd>
        </button>
      ) : (
        <button
          type="button"
          className="botao-icone"
          onClick={() => setAberto(true)}
          title="Procurar no Hub (Ctrl+K)"
          aria-label="Procurar no Hub"
        >
          <Icone nome="lupa" tamanho={18} />
        </button>
      )}

      {aberto && (
        <PainelDeBusca
          aoFechar={() => setAberto(false)}
          aoEscolher={(a) => {
            setAberto(false);
            aoNavegar(a.caminho);
          }}
        />
      )}
    </>
  );
}

function PainelDeBusca({
  aoFechar,
  aoEscolher,
}: {
  aoFechar: () => void;
  aoEscolher: (a: Achado) => void;
}) {
  const [termo, setTermo] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [procurando, setProcurando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [marcado, setMarcado] = useState(0);
  const pedido = useRef(0);
  const campo = useRef<HTMLInputElement>(null);

  usarDialogo(aoFechar);

  useEffect(() => {
    campo.current?.focus();
  }, []);

  useEffect(() => {
    const q = termo.trim();
    if (q.length < 2) {
      setResultado(null);
      setProcurando(false);
      return;
    }

    setProcurando(true);
    const meu = ++pedido.current;
    const relogio = setTimeout(() => {
      apiBusca
        .procurar(q)
        .then((r) => {
          // Chegou depois de outro pedido mais novo: descarta.
          if (meu !== pedido.current) return;
          setResultado(r);
          setErro(null);
          setMarcado(0);
        })
        .catch((e) => {
          if (meu !== pedido.current) return;
          setErro(e instanceof Error ? e.message : 'A busca não respondeu.');
          setResultado(null);
        })
        .finally(() => {
          if (meu === pedido.current) setProcurando(false);
        });
    }, ATRASO);

    return () => clearTimeout(relogio);
  }, [termo]);

  /** Os grupos achatados numa lista só, que é como a seta anda. */
  const lista = useMemo(
    () => (resultado?.grupos ?? []).flatMap((g) => g.itens),
    [resultado],
  );

  function aoTeclar(e: React.KeyboardEvent) {
    if (lista.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarcado((m) => (m + 1) % lista.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarcado((m) => (m - 1 + lista.length) % lista.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const escolhido = lista[marcado];
      if (escolhido) aoEscolher(escolhido);
    }
  }

  const q = termo.trim();

  return (
    <div
      className="cortina cortina-busca"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div className="painel-busca" role="dialog" aria-modal="true" aria-label="Procurar no Hub">
        {/* ⛔ **Nome próprio, e não `busca-campo` — 18/09/2026.** Desde a casca
            de 14/09, `busca-campo` é também o botão "Buscar" do menu lateral, e a
            folha dele vem depois: este campo herdava o fundo escuro do menu, a
            lupa sumia no branco e a moldura saía cortada no alto. */}
        <div className="busca-painel-topo">
          <span className="imv-in busca-painel-campo">
            <Icone nome="lupa" tamanho={16} />
            <input
              ref={campo}
              type="search"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={aoTeclar}
              placeholder="Cliente, caso, contrato, compromisso, anotação…"
              aria-label="O que procurar"
              /* O navegador guardaria o que foi digitado, e termo de busca aqui
                 costuma ser nome de cliente. Não fica no histórico do campo. */
              autoComplete="off"
            />
          </span>
          <button type="button" className="imv-bt fantasma p" onClick={aoFechar}>
            Fechar
          </button>
        </div>

        <div className="busca-resultado">
          {erro && <p className="erro">{erro}</p>}

          {q.length < 2 && !erro && (
            <p className="ajuda busca-dica">
              Escreva pelo menos duas letras. Acento e maiúscula não fazem diferença —
              <em> jose</em> acha <em>JOSÉ</em>.
            </p>
          )}

          {q.length >= 2 && procurando && !resultado && <p className="ajuda">Procurando…</p>}

          {q.length >= 2 && resultado && lista.length === 0 && (
            <p className="vazio">Nada encontrado para “{resultado.termo}”.</p>
          )}

          {resultado?.grupos.map((grupo) => (
            <section key={grupo.tipo} className="busca-grupo">
              <h3 className="busca-grupo-titulo lr-titulo">
                {grupo.rotulo}
                {grupo.temMais && <span className="ajuda"> · há mais, refine o termo</span>}
              </h3>
              <ul>
                {grupo.itens.map((item) => {
                  const indice = lista.indexOf(item);
                  return (
                    <li key={`${item.tipo}-${item.id}`}>
                      <button
                        type="button"
                        className={'busca-item' + (indice === marcado ? ' busca-item-marcado' : '')}
                        onMouseEnter={() => setMarcado(indice)}
                        onClick={() => aoEscolher(item)}
                      >
                        <span className="busca-item-titulo">{item.titulo}</span>
                        {item.detalhe && (
                          <span className="busca-item-detalhe">{item.detalhe}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <p className="busca-rodape ajuda">
          <strong>↑ ↓</strong> para andar · <strong>Enter</strong> para abrir ·{' '}
          <strong>Esc</strong> para fechar
        </p>
      </div>
    </div>
  );
}
